import React from 'react';
import { Asset } from 'contentful';

import { IStaticFullWidthHeroFields } from '@/types/contentful';

import { getImageData } from '../../utils/contentful';

import { Box } from '../Box';
import { Column, Layout } from '../Layout';
import CtaWidget from '../CtaWidget';

import {
  DesktopImage,
  RelativeBox,
  CustomBlockContainer,
  MobileImage,
  AdditionalContentWrapper,
  ParagraphWithLineBreaks,
  StyledIcon
} from './styles';
import PrivacyPolicyDisclaimer from 'src/components/PrivacyPolicyDisclaimer';
import { Flexbox } from '../Flexbox';
import { Text } from '../Text';
import GlImage from '../Image';
import AdditionalContent from '../AdditionalContent';
import { RenderNode } from '@contentful/rich-text-react-renderer';
import { BLOCKS } from '@contentful/rich-text-types';
import RichText from '../RichText';

import Lottie from 'lottie-react';
import { animated, useSpring, easings } from 'react-spring';
import { usePageDataContext } from '~/context/PageData';

const RICH_TEXT_OVERRIDE: RenderNode = {
  [BLOCKS.PARAGRAPH]: (_node, children) => {
    return (
      <ParagraphWithLineBreaks
        as="p"
        fontSize={{
          0: '2xs',
          tablet: 's'
        }}
        fontWeight="400"
        colour="white"
        textAlign="left"
      >
        {children}
      </ParagraphWithLineBreaks>
    );
  },
  [BLOCKS.EMBEDDED_ASSET]: (node) => (
    <StyledIcon {...getImageData(node.data.target)} />
  )
};

function useAnimatedFadeIn(key: number) {
  return useSpring({
    to: { opacity: 1, transform: 'translateY(0)' },
    from: { opacity: 0, transform: 'translateY(50px)' },
    delay: 300 + key * 500,
    config: {
      duration: 1500,
      easing: easings.easeOutExpo
    }
  });
}

export default function AnimatedFullWidthHero({
  headingText,
  subheadingText,
  desktopBackgroundImage,
  fullwidthDesktopImage,
  animatedImage,
  mobileImage,
  additionalText,
  privacyPolicyDisclaimer,
  ctaWidget,
  additionalContent,
  returningUserHeadingText
}: IStaticFullWidthHeroFields) {
  const { isReturning } = usePageDataContext();

  const visibility =
    fullwidthDesktopImage || mobileImage ? 'visible' : 'hidden';

  return (
    <Box
      data-blockid="static-fullwidth-hero"
      data-e2e="hero"
      data-testid="static-fullwidth-hero"
    >
      <div className="relative grid w-full lg:h-full lg:max-h-[624px]">
        <div style={{ gridArea: '1/1' }}>
          <GlImage
            {...getImageData(desktopBackgroundImage as Asset)}
            loading="eager"
            heroImage={false}
            layout="fill"
            objectFit="fill"
            width={undefined}
            height={undefined}
          />
        </div>
        <div
          style={{
            gridArea: '1/1',
            position: 'relative',
            placeItems: 'center',
            display: 'grid'
          }}
        >
          <CustomBlockContainer
            marginTop={{
              0: 'l',
              tablet: 'blockMargin'
            }}
            paddingBottom={
              !additionalContent
                ? {}
                : {
                    0: 'blockMarginY',
                    tablet: 'l'
                  }
            }
            revealOnScroll={false}
          >
            <Layout gutter={{ tablet: 'collapsibleVert' }} marginTop="s">
              <Column
                className="z-[1]"
                size={{ 0: 11, tablet: 5 }}
                flexDirection="column"
                justifyContent="center"
              >
                <Flexbox
                  flexDirection={{ 0: 'column' }}
                  margin={[0, 'auto']}
                  justifyContent={{ 0: 'center', tablet: 'left' }}
                  alignItems={{ 0: 'center', tablet: 'normal' }}
                >
                  <animated.div style={useAnimatedFadeIn(0)}>
                    <Text
                      as={'h1'}
                      fontWeight="700"
                      fontSize={{
                        0: 'h2_desktop',
                        tablet: 'h1_0',
                        desktop: 'h1_tablet'
                      }}
                      colour={'white'}
                      textAlign={{ 0: 'center', tablet: 'left' }}
                      margin={{
                        0: [0, 'auto', 'm', 'auto'],
                        tablet: [0, 0, 'm', 0]
                      }}
                      data-testid="fullwidth-hero-headline"
                    >
                      {isReturning
                        ? returningUserHeadingText ?? headingText
                        : headingText}
                    </Text>
                  </animated.div>
                  <animated.div style={useAnimatedFadeIn(1)}>
                    <Text
                      as="h2"
                      data-testid="fullwidth-hero-subhead"
                      fontWeight="300"
                      fontSize={{ 0: 'ml', tablet: 'xl' }}
                      colour={'white'}
                      textAlign={{ 0: 'center', tablet: 'left' }}
                      margin={{
                        0: [0, 'auto', 'm', 'auto'],
                        tablet: [0, 0, 'm', 0]
                      }}
                    >
                      {subheadingText}
                    </Text>
                  </animated.div>
                  <animated.div style={useAnimatedFadeIn(2)}>
                    {ctaWidget && (
                      <div className="mb-5 lg:mb-0">
                        <CtaWidget
                          {...ctaWidget?.fields}
                          sysId={ctaWidget?.sys?.contentType?.sys?.id}
                          id="fullwidth-hero-cta"
                          module="Hero"
                          height={56}
                          isMobile={false}
                        />
                      </div>
                    )}
                    {additionalText && (
                      <Column size={10} margin={0}>
                        <Flexbox
                          flexDirection="row"
                          justifyContent="space-between"
                          margin={0}
                          alignItems="left"
                        >
                          <AdditionalContentWrapper data-e2e="additional-content-container">
                            <RichText
                              data={additionalText}
                              overrideOptions={RICH_TEXT_OVERRIDE}
                            />
                          </AdditionalContentWrapper>
                        </Flexbox>
                      </Column>
                    )}
                    {privacyPolicyDisclaimer && (
                      <Box marginTop={{ 0: 's', tablet: 'ml' }}>
                        <PrivacyPolicyDisclaimer
                          privacyPolicyDisclaimer={privacyPolicyDisclaimer}
                          displayPrivacyPolicyIcon={false}
                          color={'white'}
                        />
                      </Box>
                    )}
                  </animated.div>
                </Flexbox>
              </Column>
              <Column
                size={{ 0: 12, tablet: 7, desktop: 6 }}
                marginTop={{ 0: 'sm', tablet: 'auto' }}
                paddingLeft={{ 0: 'sm', tablet: 'auto' }}
                paddingRight={{ 0: 'sm', tablet: 'auto' }}
              >
                <RelativeBox flex="1">
                  <animated.div style={useAnimatedFadeIn(3)}>
                    {animatedImage ? (
                      <Lottie
                        className="sm:h-[408px] md:h-[460px] lg:h-[560px]"
                        animationData={animatedImage}
                        loop={true}
                      />
                    ) : (
                      <>
                        <div className="hidden md:block">
                          <DesktopImage
                            {...getImageData(fullwidthDesktopImage as Asset)}
                            visibility={visibility}
                            loading="eager"
                            heroImage={true}
                            objectFit="contain"
                          />
                        </div>
                        <div className="block pt-3 md:hidden">
                          <MobileImage
                            {...getImageData(mobileImage as Asset)}
                            heroImage={true}
                            loading="eager"
                            data-testid="fullwidth-hero-mobile-background"
                            objectFit="contain"
                          />
                        </div>
                      </>
                    )}
                  </animated.div>
                </RelativeBox>
              </Column>
            </Layout>
          </CustomBlockContainer>
        </div>
      </div>
      <animated.div style={useAnimatedFadeIn(4)}>
        <AdditionalContent additionalContent={additionalContent} />
      </animated.div>
    </Box>
  );
}
